import { useCallback, useEffect, useMemo, useState } from 'react'
import { HotLead, ReviewStatus } from '../../types/hotLead'
import { hotLeads as seedLeads } from '../../data/hotLeads'

const REFRESH_INTERVAL_MS = 5 * 60 * 1000 // live feed runs on a ~5 minute lag
const REVIEWER = 'Levi Mahoney'

export interface HotLeadListState {
  leads: HotLead[]
  pending: HotLead[]
  reviewed: HotLead[]
  demoted: HotLead[]
  dnq: HotLead[]
  urgentCount: number
  agreementRate: number
  lastSyncedAt: Date
  isSyncing: boolean
  refresh: () => void
  agree: (id: string) => void
  override: (id: string, reasonCode: string, reasonSubcode: string) => void
  demote: (id: string, reasonCode: string, reasonSubcode: string) => void
  markDnq: (id: string, reasonCode: string, reasonSubcode: string) => void
}

export function useHotLeadList(): HotLeadListState {
  const [leads, setLeads] = useState<HotLead[]>(seedLeads)
  const [lastSyncedAt, setLastSyncedAt] = useState<Date>(new Date())
  const [isSyncing, setIsSyncing] = useState(false)

  const refresh = useCallback(() => {
    setIsSyncing(true)
    window.setTimeout(() => {
      setLastSyncedAt(new Date())
      setIsSyncing(false)
    }, 700)
  }, [])

  useEffect(() => {
    const id = window.setInterval(refresh, REFRESH_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [refresh])

  const setReview = useCallback(
    (
      id: string,
      status: ReviewStatus,
      reasonCode?: string,
      reasonSubcode?: string,
    ) => {
      setLeads((current) =>
        current.map((lead) =>
          lead.id === id
            ? {
                ...lead,
                review: {
                  status,
                  reasonCode,
                  reasonSubcode,
                  reviewedBy: REVIEWER,
                  reviewedAt: new Date().toISOString(),
                },
              }
            : lead,
        ),
      )
    },
    [],
  )

  const agree = useCallback((id: string) => setReview(id, 'agreed'), [setReview])

  const override = useCallback(
    (id: string, reasonCode: string, reasonSubcode: string) =>
      setReview(id, 'overridden', reasonCode, reasonSubcode),
    [setReview],
  )

  const demote = useCallback(
    (id: string, reasonCode: string, reasonSubcode: string) =>
      setReview(id, 'demoted', reasonCode, reasonSubcode),
    [setReview],
  )

  const markDnq = useCallback(
    (id: string, reasonCode: string, reasonSubcode: string) =>
      setReview(id, 'dnq', reasonCode, reasonSubcode),
    [setReview],
  )

  const groups = useMemo(() => {
    const pending = leads.filter((lead) => lead.review.status === 'pending')
    const reviewed = leads.filter(
      (lead) =>
        lead.review.status === 'agreed' || lead.review.status === 'overridden',
    )
    return {
      pending,
      reviewed,
      demoted: leads.filter((lead) => lead.review.status === 'demoted'),
      dnq: leads.filter((lead) => lead.review.status === 'dnq'),
    }
  }, [leads])

  const agreementRate = groups.reviewed.length
    ? Math.round(
        (groups.reviewed.filter((lead) => lead.review.status === 'agreed').length /
          groups.reviewed.length) *
          100,
      )
    : 0

  return {
    leads,
    ...groups,
    urgentCount: groups.pending.filter((lead) => lead.urgent).length,
    agreementRate,
    lastSyncedAt,
    isSyncing,
    refresh,
    agree,
    override,
    demote,
    markDnq,
  }
}
